import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import urljoin from 'url-join';

import { useStyles } from '~/assets/styles';
import { ChatProvider } from '~/components/AiChat/hooks/useChat';
import { useClaim } from '~/components/ClaimContainer';
import { ClaimSidebar } from '~/components/ClaimPage/ClaimSidebar';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { useCms } from '~/components/hooks/useCms';
import { LegalActionsContextProvider } from '~/components/LegalActions/hooks/useLegalActions';
import {
  CliveClaimContextProvider,
  useCliveClaimContext,
} from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import { SorClaimLockBanner } from '~/components/SystemOfRecord/SorClaimLockBanner';

import { useCliveClaimSidebarTabs } from '../hooks/useCliveClaimSidebarTabs';

/**
 * CliveApp Claim Page Variant
 *
 * This is a new claim page variant designed for specific segments.
 * It displays a simplified claim page with only Notes and Documents tabs.
 */
const CliveClaimPageInner: React.FC = () => {
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization, setPageTitle } = useCms();
  const { classes } = useStyles();
  const match = useRouteMatch();
  const location = useLocation();
  const { isLoading: isCliveLoading } = useCliveClaimContext();

  const tabs = useCliveClaimSidebarTabs({
    userOrganization,
    claim,
  });

  // Update page title when claim changes
  useEffect(() => {
    // @ts-expect-error - setPageTitle accepts 2 args but types say 1
    setPageTitle(`Claim: ${claim.claim_id_display}`, `Claims / ${claim.claim_id_display} — Five Sigma CMS`);
  }, [claim.claim_id_display, setPageTitle]);

  // Placeholder function for legal actions (not used in CliveApp but required by ClaimSidebar)
  const reloadLegalActions = async () => {
    // No-op for CliveApp
  };

  return (
    <>
      <SorClaimLockBanner className="mx-16 mt-8" />
      {/* Main content area with sidebar */}
      <div className="flex-1">
        <Grid container wrap="nowrap" className="h-full">
          <Grid item>
            <ClaimSidebar
              tabs={tabs}
              location={location}
              match={match}
              claim={claim}
              reloadLegalActions={reloadLegalActions}
            />
          </Grid>

          <Grid item container direction="column" className="h-full">
            <div style={{ height: '100%', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, overflow: 'auto' }}>
                <Switch>
                  <Redirect exact from={`${match.path}/`} to={urljoin(match.path, tabs[0].url)} />

                  {tabs.map((tab) => (
                    <Route
                      key={tab.url}
                      path={urljoin(match.path, tab.url)}
                      render={(routerProps) => {
                        document.title = `Claims / ${claim.claim_id_display} / ${tab.label} — Five Sigma CMS`;
                        return <tab.component classes={classes} {...routerProps} />;
                      }}
                    />
                  ))}

                  {/* Clive-gated tabs join `tabs` only once Clive loads; redirecting before would bounce them to the first tab */}
                  {!isCliveLoading ? <Redirect to={match.path} /> : null}
                </Switch>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

const CliveClaimPage = () => (
  <CliveClaimContextProvider>
    <ChatProvider>
      <LegalActionsContextProvider>
        <CliveClaimPageInner />
      </LegalActionsContextProvider>
    </ChatProvider>
  </CliveClaimContextProvider>
);

export default CliveClaimPage;
